import type { BadgeTone } from '@pv-frontend/ui-components-web';

/** pv-data's colour for a row, worst first. Mirrors the `DataHealthStatus` GraphQL enum. */
export type HealthStatus = 'RED' | 'AMBER' | 'GREEN';

/** One row of the Data health table, as the page renders it. */
export interface HealthRowView {
    kind: string;
    name: string;
    domain: string;
    status: string;
    frozen: boolean;
    attention: ReadonlyArray<string>;
    attentionDetail?: string | null;
    versionHeld?: string | null;
    publisherLatest?: string | null;
    /** When the newest OK probe of the publisher ran (pv-data's `publisher_latest_at`): the last successful check, even when nothing new was found. */
    publisherLatestAt?: string | null;
    lastSuccessAt?: string | null;
    /** The newest run's outcome, or `seeded` when the version was seeded and the job has never had a run that succeeded or failed (pv-data#369). Only `seeded` is acted on; any other value is ignored. */
    lastOutcome?: string | null;
    nextDueAt?: string | null;
    durationSeconds?: number | null;
}

export interface HealthExpiryView {
    name: string;
    domain: string;
    expiresOn: string;
    daysLeft: number;
    expired: boolean;
}

export interface HealthCountsView {
    red: number;
    amber: number;
    green: number;
    frozen: number;
    total: number;
}

export interface HealthReportView {
    generatedAt: string;
    lastWrittenAt?: string | null;
    /** The reporting database's service objective right now ("S0"), read live by the API; null when it could not be read. */
    liveTier?: string | null;
    counts: HealthCountsView;
    domains: ReadonlyArray<string>;
    scheduler?: HealthRowView | null;
    rows: ReadonlyArray<HealthRowView>;
    dueDrops: ReadonlyArray<HealthRowView>;
    expiring: ReadonlyArray<HealthExpiryView>;
    nextExpiry?: HealthExpiryView | null;
}

/** The status segments above the table. `FROZEN` is a flag across the colours, not a colour. */
export type StatusFilter = 'ALL' | 'RED' | 'AMBER' | 'GREEN' | 'FROZEN';
export const ALL_DOMAINS = '';

const RANK: Record<string, number> = { RED: 0, AMBER: 1, GREEN: 3 };
/** A colour this build does not know (a future enum value) is never healthy: it sorts with amber, after it. */
const UNKNOWN_RANK = 2;

/**
 * The strip's own order: the table's worst-first order, then paused (frozen) rows last, after the greens,
 * as in the pen. The table keeps its order; only the strip moves them.
 */
export function stripOrder<T extends { status: string; frozen: boolean }>(rows: ReadonlyArray<T>): T[] {
    const sorted = sortWorstFirst(rows);
    const paused = (r: { status: string; frozen: boolean }) => r.frozen && r.status === 'GREEN';
    return [...sorted.filter((r) => !paused(r)), ...sorted.filter(paused)];
}

/** Red, then amber, then unknown, then green; rows of one colour keep the order they came in. */
export function sortWorstFirst<T extends { status: string }>(rows: ReadonlyArray<T>): T[] {
    return rows
        .map((row, index) => ({ row, index }))
        .sort(
            (a, b) =>
                (RANK[a.row.status] ?? UNKNOWN_RANK) - (RANK[b.row.status] ?? UNKNOWN_RANK) || a.index - b.index,
        )
        .map(({ row }) => row);
}

/** Narrow the rows by domain and status segment, keeping them worst first. */
export function filterRows(
    rows: ReadonlyArray<HealthRowView>,
    domain: string,
    status: StatusFilter,
): HealthRowView[] {
    return sortWorstFirst(rows).filter((row) => {
        if (domain !== ALL_DOMAINS && row.domain.toLowerCase() !== domain.toLowerCase()) return false;
        if (status === 'ALL') return true;
        if (status === 'FROZEN') return row.frozen;
        return row.status === status;
    });
}

export function statusLook(status: string): { label: string; tone: BadgeTone } {
    switch (status) {
        case 'RED':
            return { label: 'Red', tone: 'critical' };
        case 'AMBER':
            return { label: 'Amber', tone: 'warning' };
        case 'GREEN':
            return { label: 'Green', tone: 'success' };
        default:
            return { label: 'Unknown', tone: 'neutral' };
    }
}

/** The line under a row's name: domain, then why someone should look (the detail, else pv-data's codes). */
export function rowSubline(row: HealthRowView): string {
    const why = row.attentionDetail?.trim() || row.attention.join(', ');
    return why ? `${row.domain} · ${why}` : row.domain;
}

const DAY_MS = 86_400_000;

function parse(iso: string): Date | null {
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? null : date;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "4 Aug 2026" in UK time. Month names are fixed, not the runtime's locale data (which says "Sept"). */
export function shortDate(iso: string): string {
    const date = parse(iso);
    if (!date) return iso;
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
    }).formatToParts(date);
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
    return `${get('day')} ${MONTHS[get('month') - 1]} ${get('year')}`;
}

/** "21 Oct 2026, 17:05 BST" in UK time, the zone named (GMT in winter): the silence limit is hours, so the time of day matters. */
export function shortDateTime(iso: string): string {
    const date = parse(iso);
    if (!date) return iso;
    const time = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        timeZoneName: 'short',
    }).format(date);
    return `${shortDate(iso)}, ${time}`;
}

/** "21 Oct 2026, 14:00 · 2 h ago", the age measured against `nowIso` (the read's own time), not the wall clock. */
export function tickLabel(iso: string, nowIso: string): string {
    const at = parse(iso);
    const now = parse(nowIso);
    if (!at || !now) return iso;
    const hours = Math.max(0, Math.floor((now.getTime() - at.getTime()) / 3_600_000));
    const age = hours < 48 ? `${hours} h ago` : `${Math.floor(hours / 24)} d ago`;
    return `${shortDateTime(iso)} · ${age}`;
}

/** Last success as "4 Aug 2026 · 78 d" (age against `nowIso`), or "never". */
export function lastSuccessLabel(iso: string | null | undefined, nowIso: string): string {
    if (!iso) return 'never';
    const at = parse(iso);
    const now = parse(nowIso);
    if (!at || !now) return iso;
    const days = Math.max(0, Math.floor((now.getTime() - at.getTime()) / DAY_MS));
    return `${shortDate(iso)} · ${days} d`;
}

/** "checked 24 Oct 2026 · 0 d" (age against `nowIso`) for the last successful probe; null when it has none. */
export function checkedLabel(iso: string | null | undefined, nowIso: string): string | null {
    if (!iso) return null;
    return `checked ${lastSuccessLabel(iso, nowIso)}`;
}

/**
 * True when the Last success is a `pv ledger seed` landing, not a run (pv-data#369): the source has never
 * run, so the date is when the version was seeded. Any other value (succeeded, failed, skipped, null, one
 * this build has not seen) is not seeded, and is never thrown on.
 */
export function isSeededOutcome(lastOutcome: string | null | undefined): boolean {
    return typeof lastOutcome === 'string' && lastOutcome.trim().toLowerCase() === 'seeded';
}

/** Next due: "now" once the moment has passed, else the day; a dash when nothing is scheduled. */
export function nextDueLabel(iso: string | null | undefined, nowIso: string): string {
    if (!iso) return '—';
    const at = parse(iso);
    const now = parse(nowIso);
    if (!at || !now) return iso;
    return at.getTime() <= now.getTime() ? 'now' : shortDate(iso);
}

/** "2 h 04 m", "6 m", "45 s"; a dash when the run took no recorded time. */
export function durationLabel(seconds: number | null | undefined): string {
    if (seconds === null || seconds === undefined) return '—';
    const total = Math.round(seconds);
    if (total < 60) return `${total} s`;
    const minutes = Math.floor(total / 60);
    if (minutes < 60) return `${minutes} m`;
    return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')} m`;
}

/** "Rail Data Marketplace push SAS · 28 Sep 2027 (342 d)" for the expiring panel's empty state. */
export function expiryLabel(expiry: HealthExpiryView): string {
    return `${expiry.name} · ${shortDate(expiry.expiresOn)} (${expiry.daysLeft} d)`;
}

/** pv-data's attention code for a burst tier still up past its idle settle (also raised read-side by the API). */
export const BURST_OVERDUE_CODE = 'burst tier past idle settle';

/** What the Scheduler panel says, derived from the scheduler row (pv-data `scheduler` row; `next_due_at` there is the idle settle due). */
export interface SchedulerView {
    /** The tier and what it is doing, exactly as pv-data recorded `version_held` ("S4 (burst, idle)"); null when the row has none (not UAT). */
    tier: string | null;
    /** What the Tier line says: the recorded text, or, when the live tier differs, the live truth first ("S0 (recorded S4, idle)"). Null when the row has no tier. */
    tierLine: string | null;
    /** The live tier when it differs from the recorded one ("S0" while the record says S4); null when they agree or it was not read. */
    liveDiffers: string | null;
    /** True when the API could not read the live tier, so the recorded tier is all there is. */
    liveUnread: boolean;
    /** "idle settle due 24 Oct 2026, 11:30 BST" while the due time is ahead and the live tier still agrees; null otherwise. */
    settleLine: string | null;
    /** True when no tick has ever been recorded: the row exists only because the tier scaler left a mark. */
    neverTicked: boolean;
    /** "S4 left up past its idle settle" when the API flagged a burst tier past its settle; null otherwise. */
    overdueHeadline: string | null;
    /** The facts under the headline: the API's detail without its own "<code>: " prefix and without markdown backticks. */
    detail: string | null;
}

const BURST_MARKER = ' (burst';

export function isBurstTier(versionHeld: string | null | undefined): boolean {
    return typeof versionHeld === 'string' && versionHeld.includes(BURST_MARKER);
}

/** "S4" from "S4 (burst, idle)". */
function tierName(versionHeld: string | null): string | null {
    if (!versionHeld) return null;
    const at = versionHeld.indexOf(' (');
    return at > 0 ? versionHeld.slice(0, at) : versionHeld;
}

/**
 * The overdue judgement is the API's alone (pv-mono `DataHealthBuilder.JudgeScheduler`: the LIVE tier plus a grace for
 * the Azure settle runbook). The page never recomputes it from the recorded `next_due_at`, which goes stale the moment
 * the runbook scales the database down; it renders the attention code the API left on the row.
 */
export function schedulerView(row: HealthRowView, nowIso: string, liveTier?: string | null): SchedulerView {
    const held = row.versionHeld?.trim() || null;
    const recorded = tierName(held);
    const live = liveTier?.trim() || null;
    const liveDiffers = live && recorded && live.toLowerCase() !== recorded.toLowerCase() ? live : null;
    const due = row.nextDueAt ? parse(row.nextDueAt) : null;
    const now = parse(nowIso);
    const burst = isBurstTier(held);
    const overdue = burst && row.attention.includes(BURST_OVERDUE_CODE);
    const ahead = due !== null && now !== null && due.getTime() >= now.getTime();
    const detail = row.attentionDetail
        ?.replace(new RegExp(`^${BURST_OVERDUE_CODE}: `), '')
        .replace(/`/g, '')
        .trim();
    const state = held?.match(/\(([^)]*)\)/)?.[1]?.replace(/^burst,\s*/, '').trim();
    const tierLine = held
        ? liveDiffers
            ? `${liveDiffers} (recorded ${recorded}${state ? `, ${state}` : ''})`
            : held
        : null;
    return {
        tier: held,
        tierLine,
        liveDiffers,
        liveUnread: live === null && held !== null,
        settleLine: burst && row.nextDueAt && ahead && !liveDiffers ? `idle settle due ${shortDateTime(row.nextDueAt)}` : null,
        neverTicked: !row.lastOutcome && !row.lastSuccessAt,
        overdueHeadline: overdue && (liveDiffers ?? recorded) ? `${liveDiffers ?? recorded} left up past its idle settle` : null,
        detail: detail || null,
    };
}
